<template>
    <div class="bar-line-chart-template">
        <canvas :id="'chart-'+id" :class="(if_chart_has_data ? '':'hide')" class="bar-line-chart chart-template" ></canvas>
        <div :class="(!if_chart_has_data && !if_refreshing ? 'd-inline-block':'hide')" class='text-center'>
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent, markRaw } from 'vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chartTooltip from '../../../../mixins/chart_tooltip';
import chartBase from '../../../../mixins/chart_base';

export default defineComponent({
    name: 'BarLineComboChartTemplateNew',
    mixins: [chartTooltip, chartBase],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        id: {
            type: [String, Number],
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        if_show_label: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'small'
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        show_compressed_numbers: {
            type: Boolean,
            default: true
        },
        forceShowAllData: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            color: null,
            format_type: [],
            max_bar_value: 0,
            max_line_value: 0,
            tooltipHideTimeout: null,
            hoverDebounceTimeout: null,
            datasetVisibility: null,
            /** When true, next chart render (e.g. after resize) uses no animation. Cleared after render. */
            skipAnimationForNextRender: false,
        };
    },
    watch: {
        size(newSize, oldSize) {
            if (newSize !== oldSize) {
                this.skipAnimationForNextRender = true;
            }
        },
    },
    beforeUnmount() {
        // Clear component-specific timeouts
        if (this.tooltipHideTimeout) {
            clearTimeout(this.tooltipHideTimeout);
            this.tooltipHideTimeout = null;
        }
        if (this.hoverDebounceTimeout) {
            clearTimeout(this.hoverDebounceTimeout);
            this.hoverDebounceTimeout = null;
        }
        // Parent beforeUnmount from chartBase mixin will be called automatically
    },
    methods: {        
        getVisibleDatasetCount() {
            if (this.chart?.data?.datasets && typeof this.chart.isDatasetVisible === 'function') {
                let count = 0;
                for (let i = 0; i < this.chart.data.datasets.length; i++) {
                    if (this.chart.isDatasetVisible(i)) count++;
                }
                return count || 1;
            }
            return this.num_of_datasets || 1;
        },
        isDatasetHiddenByDefault(index) {
            if (this.datasetVisibility && this.datasetVisibility.length === this.num_of_datasets && this.datasetVisibility[index] !== undefined) {
                return !this.datasetVisibility[index];
            }
            return false;
        },
        /**
         * Calculate how many labels (categories) to show based on chart size.
         */
        calculateLabelsToShow() {
            const chartSize = this.size || this.chart_selected?.size || 'small';
            const totalLabels = this.chart_selected.labels?.length || 0;
            const visibleDatasets = this.getVisibleDatasetCount();
            
            if (chartSize === 'small') {
                return Math.min(2, totalLabels);
            }
            if (chartSize === 'medium') {
                return Math.min(5, totalLabels);
            }
            if (chartSize === 'large' || chartSize === 'x-large') {
                // x-large uses same chart width as large (col-4 definitions on the right)
                if (totalLabels < 15) {
                    return totalLabels;
                }
                const labelsToShow = Math.max(1, Math.floor(15 / visibleDatasets));
                return Math.min(labelsToShow, totalLabels);
            }
            return totalLabels;
        },
        /**
         * Override setupChartMouseLeave to use hoverDebounceTimeout instead of hideTimeout
         */
        setupChartMouseLeave(chartInstance) {
            const canvas = chartInstance.canvas;
            if (!canvas) return;

            canvas.addEventListener('mouseleave', () => {
                // Debounce hiding the hover state (500ms delay to match tooltip)
                if (this.hoveredIndex !== null && !this.hoverDebounceTimeout) {
                    this.hoverDebounceTimeout = setTimeout(() => {
                        // Only hide if still not hovering and tooltip is also not being hovered
                        if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                            this.hoveredIndex = null;
                            this.animateHoverOpacity(0, chartInstance);

                            // Explicitly hide tooltip element to stay in sync
                            const tooltipEl = document.getElementById('chart-' + this.id + '-' + this.effectiveSectionId + '-tooltip');
                            if (tooltipEl) {
                                tooltipEl.style.opacity = 0;
                                tooltipEl.style.pointerEvents = 'none';
                            }
                        }
                        this.hoverDebounceTimeout = null;
                    }, 200);
                }
            });
        },

        renderChart() {
            if (!this.chart_selected?.data) return;

            this.num_of_datasets = Object.keys(this.chart_selected.data).length;
            if (this.datasetVisibility && this.datasetVisibility.length !== this.num_of_datasets) {
                this.datasetVisibility = null;
            }
            this.totalDataLength = this.chart_selected.labels?.length || 0;
            this.displayedDataCount = this.forceShowAllData ? this.totalDataLength : this.calculateLabelsToShow();
            this.isDataTruncated = this.displayedDataCount < this.totalDataLength;

            if (this.isTimeBased() && this.isDataTruncated && !this.pageInitialized) {
                this.currentPageStart = Math.max(0, this.totalDataLength - this.displayedDataCount);
            }
            this.pageInitialized = true;

            // Emit state to parent for centralized pagination controls
            this.emitPaginationState();

            // Get full dataset first (will use displayedDataCount for styling)
            const fullDataset = this.getChartData();
            
            // Apply truncation
            const dataset = this.getTruncatedData(fullDataset);
            
            const options = this.getChartOptions(dataset);

            // First destroy any existing chart
            this.destroyChart();

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const ctx = document.getElementById('chart-' + this.id);
                if (!ctx?.getContext) {
                    console.warn('Chart canvas not found or context not available');
                    return;
                }

                try {
                    // Use helper to wait until the canvas is visible before creating the chart
                    this.createChartWhenVisible(ctx, options, (chartInstance) => {
                        this.chart = markRaw(chartInstance);
                        // Setup mouseleave handler (overridden method)
                        this.setupChartMouseLeave(chartInstance);
                        this.skipAnimationForNextRender = false;
                    });
                } catch (error) {
                    // Only log error if it's not a rate limit error
                    if (!error.message?.includes('429')) {
                        console.error('Error creating chart:', error);
                    }
                }
            });
        },
        debouncedRender() {
            if (this.render_timeout) {
                clearTimeout(this.render_timeout);
            }
            this.render_timeout = setTimeout(() => {
                this.$nextTick(() => {
                    if(this.chart_selected.metric_formats){
                        this.format_type = this.chart_selected.metric_formats
                        var color_array = [];
                        this.chart_selected.metrics.forEach((m)=>{
                            color_array.push(m.color);
                        })
                        color_array[0] = 'gray'; // the first metric is shown as bar in gray scale
                        this.color = color_array;
                    } else {
                        this.format_type = ['number', 'number']; // bar_line combo chart should have at least 2 metrics
                        this.color = ['gray', 'yellow']; // the first metric is shown as bar in gray scale
                    }

                    this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                    this.renderChart();
                    this.if_chart_has_data = true;
                    // Re-emit pagination state now that chart has data
                    this.emitPaginationState();
                });
            }, 200); // 200ms debounce
        },
        /**
         * Animates hover opacity from current value to target value
         * @param {number} targetOpacity - Target opacity (0 to 1)
         * @param {Object} chart - Chart.js instance
         */
        /**
         * Override emitPaginationState to emit pagination text
         */
        emitPaginationState() {
            this.$emit('update:pagination', {
                isTruncated: this.isDataTruncated && this.if_chart_has_data,
                currentPageStart: this.currentPageStart,
                displayedDataCount: this.displayedDataCount,
                totalDataLength: this.totalDataLength,
                paginationText: this.getPaginationText()
            });
        },
        /**
         * Darkens a color by a percentage (0-1)
         * @param {string} color - RGB color string like 'rgb(255 255 255)' or 'rgb(255, 255, 255)'
         * @param {number} percent - Percentage to darken (0.2 = 20%)
         * @returns {string} Darkened RGB color string
         */
        /**
         * Calculates a nice rounded-up maximum value for the y-axis
         * Logic: if (top_axis - highest_bar/point) < 35% of the space between axis ticks, add additional top axis
         */
        getTopBorderRadiusByDataLength(dataLength) {
            if (dataLength < 5) {
                return 16;
            } else if (dataLength < 10) {
                return 8;
            } else if (dataLength < 15) {
                return 4;
            } else {
                return 0;
            }
        },
        /**
         * Calculates bar thickness based on chart size and data length
         */
        // getDataLabelSizeByDataLength(dataLength) {
        //     if (dataLength <= 5) {
        //         return 12;
        //     } else if (dataLength < 10) {
        //         return 10;
        //     } else if (dataLength < 15) {
        //         return 8;
        //     } else {
        //         return 6;
        //     }
        // },
        getLegendLabelStylesByDatasetType(dataset, index) {
            const defaultStyle = {
                text: dataset.label,
                fillStyle: dataset.backgroundColor[0],
                strokeStyle: dataset.backgroundColor[0],
                pointStyleWidth: 16,
                lineWidth: 0, // remove box border by default
                hidden: false,
            };
            let style = defaultStyle;
            switch (dataset.type) {
                case 'pie':
                    style.pointStyle = 'circle';
                    break;
                case 'bar':
                    style.pointStyle = 'circle';
                    break;
                case 'line':
                    style.pointStyle = 'line';
                    style.lineWidth = 2;
                    style.pointStyleWidth = 20;
                    style.borderWidth = this.if_benchmark_report? 3 : 1.5;
                    // Set text color to metric color (line color) for line datasets
                    const lineColor = Array.isArray(dataset.borderColor) ? dataset.borderColor[0] : dataset.borderColor;
                    // style.fontColor = lineColor || this.getColorByLetter(this.color[1], 'A');
                    // Use borderColor for the legend line color
                    style.fillStyle = lineColor || this.getColorByLetter(this.color[1], 'A');
                    style.strokeStyle = lineColor || this.getColorByLetter(this.color[1], 'A');
                    break;
                default:
                    break;
            }
            return style;
        },
        getChartData() {
            var datasetArray = [];
            var num_of_datasets = Object.keys(this.chart_selected.data).length;
            var element_on_scorecard = document.querySelector('.scorecard-page');
            var max_bar = 0; // Max value for bar data (y axis)
            var max_line = 0; // Max value for line data (y1 axis)
            
            if (this.if_chart_filter_applied) {
                if (this.chart_filter_label.length > 1) { // multiple dimension
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                    this.chart_filter_label_index[1] = Object.keys(this.chart_selected.data).findIndex(data => (data == "" ? "(not set)" : data) == this.chart_filter_label[1]);
                } else { // single dimension
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                    this.chart_filter_label_index[1] = null;
                }
            }

            // Get color indices based on number of datasets (excluding first dataset which is gray bar)
            const lineDatasetCount = num_of_datasets - 1;
            const colorIndices = this.getColorIndicesForDatasets(lineDatasetCount);
            const colorArray = this.getColor(this.color[1]);
            // Map indices to actual color values
            const chartColors = colorIndices.map(idx => colorArray[idx]);

            Object.keys(this.chart_selected.data).forEach((key, index) => {
                var max_temp = Math.max(...this.chart_selected.data[key]);
                
                // Calculate separate max values for bar and line data
                if (index === 0) {
                    // Bar data (first dataset)
                    if (max_temp > max_bar) {
                        max_bar = max_temp;
                    }
                } else {
                    // Line data (subsequent datasets)
                    if (max_temp > max_line) {
                        max_line = max_temp;
                    }
                }

                if (index == 0) { // first dataset will be plotted as grayscale bar
                    var backgroundColorArray = [];
                    const grayD = this.getColorByLetter('gray', 'D');
                    backgroundColorArray = Array(this.chart_selected.data[key].length).fill(grayD);

                    if (this.if_chart_filter_applied) {
                        if (this.chart_filter_label.length > 1) {
                            if (index == this.chart_filter_label_index[1]) {
                                backgroundColorArray[this.chart_filter_label_index[0]] = this.getColorByLetter('gray', 'A');
                            }
                        } else {
                            backgroundColorArray[this.chart_filter_label_index[0]] = this.getColorByLetter('gray', 'A');
                        }
                    } else if (element_on_scorecard && this.chart_selected.data[key].length >= 3) {
                        for (var i = backgroundColorArray.length - 1; i >= backgroundColorArray.length - 3; i--) {
                            backgroundColorArray[i] = 'rgba(' + grayD.substring(4, grayD.length - 1) + ', 0.5)';
                        }
                    }

                    const dataLength = this.chart_selected.data[key].length;
                    const barThickness = this.getBarThickness(dataLength);

                    datasetArray.push({
                        index: index,
                        label: key == "" ? '(not set)' : key,
                        data: this.chart_selected.data[key],
                        backgroundColor: backgroundColorArray,
                        borderRadius: {
                            topLeft: this.getTopBorderRadiusByDataLength(dataLength), // round the top corners
                            topRight: this.getTopBorderRadiusByDataLength(dataLength), // round the top corners
                            bottomLeft: 0,
                            bottomRight: 0,
                        },
                        maxBarThickness: barThickness,
                        order: 5, // higher order means drawing behind; always render bar data behind the line data
                        yAxisID: 'y',
                        hidden: this.isDatasetHiddenByDefault(index),
                    })

                } else { // second and onward dataset will be plotted as line
                    var backgroundColorArray = [];
                    const lineIndex = index - 1; // Adjust index for line datasets (exclude first bar dataset)
                    // Get color for this dataset (reverse order: last dataset gets first color)
                    const datasetColorIndex = chartColors.length - 1 - lineIndex;
                    const datasetColor = chartColors[datasetColorIndex] || chartColors[0];
                    const colorA = this.getColorByLetter(this.color[1], 'A');
                    
                    backgroundColorArray = Array(this.chart_selected.data[key].length).fill(colorA);

                    if (element_on_scorecard) {
                        const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                        datasetArray.push({
                            index: index,
                            label: key == "" ? '(not set)' : key,
                            data: this.chart_selected.data[key],
                            pointRadius: basePointRadius,
                            pointHoverRadius: basePointRadius,
                            pointBorderColor: 'white',
                            pointBorderWidth: 1,
                            backgroundColor: backgroundColorArray,
                            borderColor: backgroundColorArray,
                            borderWidth: this.if_benchmark_report ? 3 : 1.5,
                            borderDash: [],
                            borderRadius: 3,
                            type: 'line',
                            segment: {
                                borderDash: ctx => this.projected(ctx, this.chart_selected.data[key].length, index, [10, 5]),
                            },
                            order: index + 1,
                            yAxisID: 'y1',
                            isMetricColorDataset: index == num_of_datasets - 1,
                            hidden: this.isDatasetHiddenByDefault(index),
                        })
                    } else if (this.if_chart_filter_applied) {
                        // Map index to gray color (reverse: index 0 -> G, index 1 -> F, etc.)
                        const grayIndex = Math.max(0, Math.min(6 - lineIndex, 6));
                        const grayLetter = ['G', 'F', 'E', 'D', 'C', 'B', 'A'][grayIndex] || 'A';
                        const grayColor = this.getColorByLetter('gray', grayLetter);
                        const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                        
                        let finalBorderColor = grayColor;
                        let isMetricColorDataset = false;
                        let metricColorPointIndex = undefined;

                        // Use gray for non-highlighted points
                        const grayD = this.getColorByLetter('gray', 'D');
                        var filterBackgroundColorArray = Array(this.chart_selected.data[key].length).fill(grayD);

                        if (this.chart_filter_label.length > 1) {
                            if (index == this.chart_filter_label_index[1]) {
                                finalBorderColor = datasetColor;
                                isMetricColorDataset = false;
                                metricColorPointIndex = this.chart_filter_label_index[0];
                                
                                // Only highlight the filtered point on the matching dataset
                                if (metricColorPointIndex >= 0) {
                                    filterBackgroundColorArray[metricColorPointIndex] = colorA;
                                }
                            }
                        } else {
                            // Single dimension: highlight the point on all lines
                            metricColorPointIndex = this.chart_filter_label_index[0];
                            if (metricColorPointIndex >= 0) {
                                filterBackgroundColorArray[metricColorPointIndex] = colorA;
                            }
                        }

                        datasetArray.push({
                            index: index,
                            label: key == "" ? '(not set)' : key,
                            data: this.chart_selected.data[key],
                            pointRadius: basePointRadius,
                            pointHoverRadius: basePointRadius,
                            pointBorderColor: 'white',
                            pointBorderWidth: 1,
                            backgroundColor: filterBackgroundColorArray,
                            borderColor: finalBorderColor,
                            borderWidth: this.if_benchmark_report ? 3 : 1.5,
                            borderDash: [],
                            borderRadius: 3,
                            type: 'line',
                            order: index + 1,
                            yAxisID: 'y1',
                            isMetricColorDataset: isMetricColorDataset,
                            metricColorPointIndex: metricColorPointIndex,
                            hidden: this.isDatasetHiddenByDefault(index),
                        })
                    } else if (this.chart_selected.highlight_last_value) {
                        const isLastDataset = index == num_of_datasets - 1;
                        let backgroundColor, borderColor;
                        if (isLastDataset) {
                            backgroundColor = colorA;
                            borderColor = colorA;
                        } else {
                            // Map index to gray color (reverse: index 0 -> G, index 1 -> F, etc.)
                            const grayIndex = Math.max(0, Math.min(6 - lineIndex, 6));
                            const grayLetter = ['G', 'F', 'E', 'D', 'C', 'B', 'A'][grayIndex] || 'A';
                            backgroundColor = this.getColorByLetter('gray', grayLetter);
                            borderColor = this.getColorByLetter('gray', grayLetter);
                        }
                        const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                        datasetArray.push({
                            index: index,
                            label: key == "" ? '(not set)' : key,
                            data: this.chart_selected.data[key],
                            pointRadius: basePointRadius,
                            pointHoverRadius: basePointRadius,
                            pointBorderColor: 'white',
                            pointBorderWidth: 1,
                            backgroundColor: backgroundColor,
                            borderColor: borderColor,
                            borderWidth: this.if_benchmark_report ? 3 : 1.5,
                            borderDash: [],
                            borderRadius: 3,
                            type: 'line',
                            order: index + 1,
                            yAxisID: 'y1',
                            isMetricColorDataset: isLastDataset,
                            hidden: this.isDatasetHiddenByDefault(index),
                        })
                    } else {
                        const isLastDataset = index == num_of_datasets - 1;
                        const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                        datasetArray.push({
                            index: index,
                            label: key == "" ? '(not set)' : key,
                            data: this.chart_selected.data[key],
                            pointRadius: basePointRadius,
                            pointHoverRadius: basePointRadius,
                            pointBorderColor: 'white',
                            pointBorderWidth: 1,
                            backgroundColor: datasetColor,
                            borderColor: datasetColor,
                            borderWidth: this.if_benchmark_report ? 3 : 1.5,
                            borderDash: [],
                            borderRadius: 3,
                            type: 'line',
                            order: index + 1,
                            yAxisID: 'y1',
                            isMetricColorDataset: isLastDataset,
                            hidden: this.isDatasetHiddenByDefault(index),
                        })
                    }
                }
            });
            this.max_value = max_bar; // Keep for backward compatibility (used in formatting)
            this.max_bar_value = max_bar;
            this.max_line_value = max_line;

            var labels_temp = [];
            this.chart_selected.labels.forEach((label) => {
                if (label) {
                    labels_temp.push(label)
                } else {
                    labels_temp.push('(not set)')
                }
            });

            return {
                labels: labels_temp,
                datasets: datasetArray
            };
        },
        projected(ctx, total_length, index, value) {
            return (total_length-1) - ctx.p0DataIndex <=3 ? value : undefined;
        },
        getChartOptions(data) {
            var self = this;

            // Custom plugin to draw background color for hovered group
            const hoverBackgroundPlugin = {
                id: 'hoverBackground',
                beforeDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null && chart.scales.x) {
                        const ctx = chart.ctx;
                        const xScale = chart.scales.x;
                        const yScale = chart.scales.y;
                        
                        // Get shade E of gray color (since bar is gray) with 30% opacity
                        const colorE = self.getColorByLetter('gray', 'E');
                        // Handle both formats: rgb(r g b) and rgb(r, g, b)
                        const rgbMatch = colorE.match(/rgb\((\d+)[,\s]+(\d+)[,\s]+(\d+)\)/);
                        
                        if (rgbMatch) {
                            const r = rgbMatch[1];
                            const g = rgbMatch[2];
                            const b = rgbMatch[3];
                            const rgbaColor = `rgba(${r}, ${g}, ${b}, ${0.3 * self.hoverOpacity})`;
                            
                            // Get the pixel positions for the hovered category and adjacent categories
                            const hoveredPixelValue = xScale.getPixelForValue(self.hoveredIndex);
                            const totalWidth = xScale.width;
                            const numCategories = xScale.ticks.length;
                            const categorySpacing = totalWidth / numCategories;
                            
                            // Calculate left edge: midpoint between previous category and hovered category
                            let leftEdge;
                            if (self.hoveredIndex > 0) {
                                const prevPixelValue = xScale.getPixelForValue(self.hoveredIndex - 1);
                                leftEdge = (prevPixelValue + hoveredPixelValue) / 2;
                            } else {
                                // For first category, use midpoint between start and current
                                leftEdge = hoveredPixelValue - categorySpacing / 2;
                            }
                            
                            // Calculate right edge: midpoint between hovered category and next category
                            let rightEdge;
                            if (self.hoveredIndex < numCategories - 1) {
                                const nextPixelValue = xScale.getPixelForValue(self.hoveredIndex + 1);
                                rightEdge = (hoveredPixelValue + nextPixelValue) / 2;
                            } else {
                                // For last category, use midpoint between current and end
                                rightEdge = hoveredPixelValue + categorySpacing / 2;
                            }
                            
                            // Clamp to chart area so overlay never covers y-axis or x-axis labels
                            const chartLeft = chart.chartArea.left;
                            const chartRight = chart.chartArea.right;
                            const leftEdgeClipped = Math.max(leftEdge, chartLeft);
                            const rightEdgeClipped = Math.min(rightEdge, chartRight);
                            
                            // Calculate x position and width for the wider highlight box
                            const x = leftEdgeClipped;
                            const categoryWidth = rightEdgeClipped - leftEdgeClipped;
                            const y = yScale.top;
                            
                            // Extend height to include axis labels
                            // Get the bottom of the x-axis scale which includes the label area
                            const xAxisBottom = xScale.bottom || chart.chartArea.bottom;
                            const height = xAxisBottom - yScale.top;
                            
                            // Draw rounded rectangle with 30px border radius
                            ctx.save();
                            ctx.fillStyle = rgbaColor;
                            const radius = 30;
                            ctx.beginPath();
                            ctx.moveTo(x + radius, y);
                            ctx.lineTo(x + categoryWidth - radius, y);
                            ctx.quadraticCurveTo(x + categoryWidth, y, x + categoryWidth, y + radius);
                            ctx.lineTo(x + categoryWidth, y + height - radius);
                            ctx.quadraticCurveTo(x + categoryWidth, y + height, x + categoryWidth - radius, y + height);
                            ctx.lineTo(x + radius, y + height);
                            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
                            ctx.lineTo(x, y + radius);
                            ctx.quadraticCurveTo(x, y, x + radius, y);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        }
                    }
                }
            };

            // Custom plugin to darken hovered bars
            const darkenBarsPlugin = {
                id: 'darkenBars',
                afterDraw: (chart) => {
                    if (self.hoveredIndex !== null) {
                        const ctx = chart.ctx;
                        
                        // Iterate through all datasets
                        chart.data.datasets.forEach((dataset, datasetIndex) => {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            if (!meta || meta.hidden) return;
                            if (dataset.type !== 'bar') return; // Only process bar datasets
                            
                            // Get the bar element for the hovered index
                            const bar = meta.data[self.hoveredIndex];
                            if (!bar) return;
                            
                            // Get the original color
                            const originalColor = Array.isArray(dataset.backgroundColor)
                                ? dataset.backgroundColor[self.hoveredIndex]
                                : dataset.backgroundColor;
                            
                            // Darken the color by 10%
                            const darkerColor = self.darkenColor(originalColor, 0.1);
                            
                            // Get bar properties
                            const { x, y, base, width, height } = bar.getProps(['x', 'y', 'base', 'width', 'height']);
                            
                            // Get border radius from the dataset
                            const borderRadius = dataset.borderRadius || {};
                            const topLeftRadius = borderRadius.topLeft || 0;
                            
                            // Calculate actual border radius value
                            const actualRadius = Math.min(topLeftRadius, width / 2, Math.abs(height) / 2);
                            
                            // Draw the darkened bar on top
                            ctx.save();
                            ctx.fillStyle = darkerColor;
                            
                            // Draw rounded rectangle (only top corners rounded)
                            const barHeight = y - base;
                            const barY = Math.min(y, base);
                            const barX = x - width / 2;
                            const barBottom = barY + Math.abs(barHeight);
                            
                            ctx.beginPath();
                            // Start at top-left (rounded)
                            ctx.moveTo(barX + actualRadius, barY);
                            // Top edge
                            ctx.lineTo(barX + width - actualRadius, barY);
                            // Top-right corner (rounded)
                            ctx.quadraticCurveTo(barX + width, barY, barX + width, barY + actualRadius);
                            // Right edge (straight to bottom)
                            ctx.lineTo(barX + width, barBottom);
                            // Bottom-right corner (sharp)
                            ctx.lineTo(barX, barBottom);
                            // Bottom-left corner (sharp)
                            ctx.lineTo(barX, barY + actualRadius);
                            // Top-left corner (rounded)
                            ctx.quadraticCurveTo(barX, barY, barX + actualRadius, barY);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        });
                    }
                }
            };

            // Custom plugin to draw dots on top of all lines and make metric color dots bigger on hover
            const enlargeMetricColorPointPlugin = {
                id: 'enlargeMetricColorPoint',
                beforeDatasetsDraw: (chart) => {
                    // Hide original points to draw them on top later in afterDatasetsDraw
                    chart.data.datasets.forEach((dataset) => {
                        if (dataset.type !== 'line') return;
                        if (dataset.data.length > 60) return;
                        
                        // Store original radius if not already stored
                        if (dataset._originalPointRadius === undefined) {
                            dataset._originalPointRadius = dataset.pointRadius;
                        }
                        // Temporarily set pointRadius to 0 to hide default points
                        dataset.pointRadius = 0;
                    });
                },
                afterDatasetsDraw: (chart) => {
                    const ctx = chart.ctx;
                    
                    chart.data.datasets.forEach((dataset, datasetIndex) => {
                        const meta = chart.getDatasetMeta(datasetIndex);
                        // Only process visible line datasets
                        if (!meta || meta.hidden || !chart.isDatasetVisible(datasetIndex)) return;
                        if (dataset.type !== 'line') return;
                        
                        // Skip if data length > 60 (points are hidden)
                        if (dataset.data.length > 60) return;
                        
                        const isMetricColorDataset = dataset.isMetricColorDataset === true;
                        const metricColorPointIndex = dataset.metricColorPointIndex;
                        
                        // Use stored original radius or default
                        const baseRadius = typeof dataset._originalPointRadius === 'number' 
                            ? dataset._originalPointRadius 
                            : 4;

                        meta.data.forEach((point, dataIndex) => {
                            if (!point) return;
                            
                            // Get point properties
                            const { x, y } = point.getProps(['x', 'y']);
                            
                            // Get the point color
                            let pointColor;
                            if (Array.isArray(dataset.backgroundColor)) {
                                pointColor = dataset.backgroundColor[dataIndex];
                            } else {
                                pointColor = dataset.backgroundColor;
                            }
                            
                            // Check if this point is currently hovered
                            const isHoveredIndex = self.hoveredIndex === dataIndex;
                            
                            // Determine if this point should be enlarged (metric color dataset or specific point)
                            let shouldEnlarge = false;
                            if (isMetricColorDataset) {
                                shouldEnlarge = true;
                            } else if (metricColorPointIndex !== undefined && metricColorPointIndex === dataIndex) {
                                shouldEnlarge = true;
                            }
                            
                            // Determine point radius and border width
                            let radius = baseRadius;
                            let borderWidth = 1;
                            
                            if (isHoveredIndex) {
                                // Enlarged state during hover
                                radius = shouldEnlarge ? 7 : 5;
                                borderWidth = 2;
                            }

                            if (radius <= 0) return;
                            
                            // Draw the point with white border
                            ctx.save();
                            
                            // Draw fill
                            ctx.fillStyle = pointColor;
                            ctx.beginPath();
                            ctx.arc(x, y, radius, 0, Math.PI * 2);
                            ctx.fill();
                            
                            // Draw white border
                            ctx.strokeStyle = 'white';
                            ctx.lineWidth = borderWidth;
                            ctx.beginPath();
                            ctx.arc(x, y, radius, 0, Math.PI * 2);
                            ctx.stroke();
                            
                            ctx.restore();
                        });
                    });
                }
            };

            // Custom plugin to draw solid line at y=0 (base x-axis) extending across chart area including x-axis labels
            const solidBaseLinePlugin = {
                id: 'solidBaseLine',
                beforeDatasetsDraw: (chart) => {
                    const ctx = chart.ctx;
                    const yScale = chart.scales.y;
                    const xScale = chart.scales.x;
                    
                    // Get the y position for zero
                    const zeroY = yScale.getPixelForValue(0);
                    
                    // Use chart area boundaries and extend slightly to cover x-axis labels
                    // Start from the left edge of the chart area (where bars start)
                    // End at the right edge of the chart area, extending slightly to cover labels
                    const leftEdge = chart.chartArea.left;
                    const rightEdge = xScale.right; // Extend to cover x-axis labels
                    
                    // Draw solid line extending across the chart area and x-axis labels
                    ctx.save();
                    ctx.strokeStyle = 'rgba(206, 219, 215, 1)';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([]); // Solid line - empty array means no dashes
                    ctx.beginPath();
                    ctx.moveTo(leftEdge, zeroY);
                    ctx.lineTo(rightEdge, zeroY);
                    ctx.stroke();
                    ctx.restore();
                }
            };

            var config = {
                plugins: [solidBaseLinePlugin, hoverBackgroundPlugin, darkenBarsPlugin, enlargeMetricColorPointPlugin, ChartDataLabels],
                type: 'bar',
                data: data,

                //All of the special aspects that make these charts unique
                options: {
                    interaction: {
                        intersect: false,
                        mode: 'index',
                    },
                    onHover: (event, activeElements, chart) => {
                        if (activeElements && activeElements.length > 0) {
                            // Clear any pending hide timeout when hovering over an element
                            if (this.hideTimeout) {
                                clearTimeout(this.hideTimeout);
                                this.hideTimeout = null;
                            }
                            
                            const activeElement = activeElements[0];
                            const dataIndex = activeElement.index;
                            
                            // If hovering over a different data point OR returning to a point that's not currently hovered,
                            // (re)start the debounce timer
                            if (dataIndex !== this.pendingHoverIndex || this.hoveredIndex !== dataIndex) {
                                // Cancel any pending show timeout
                                if (this.hoverTimeout) {
                                    clearTimeout(this.hoverTimeout);
                                }
                                
                                // Reset current state immediately when moving to a new point
                                // to keep highlight and tooltip in sync (both hidden during the debounce)
                                if (this.hoveredIndex !== null && this.hoveredIndex !== dataIndex) {
                                    this.hoveredIndex = null;
                                    this.hoverOpacity = 0;
                                    chart.update('none');
                                }
                                
                                // Reset flags for new data point
                                this.pendingHoverIndex = dataIndex;
                                
                                // Start new debounce timer - 500ms delay to show hover
                                this.hoverTimeout = setTimeout(() => {
                                    this.hoverTimeout = null;
                                    // Apply hover effect after delay
                                    if (this.hoveredIndex !== dataIndex) {
                                        this.hoveredIndex = dataIndex;
                                        chart.update('none'); // Immediate update for point size changes
                                        this.animateHoverOpacity(1, chart);
                                    }
                                }, 200);
                            }
                        } else {
                            // Clear any pending show timeout when leaving
                            if (this.hoverTimeout) {
                                clearTimeout(this.hoverTimeout);
                                this.hoverTimeout = null;
                                this.pendingHoverIndex = null;
                            }
                            
                            // Debounce hiding the hover state (500ms delay)
                            // This matches the show debounce timing for consistency
                            if (this.hoveredIndex !== null && !this.hideTimeout) {
                                this.hideTimeout = setTimeout(() => {
                                    // Only hide if still not hovering and tooltip is also not being hovered
                                    if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                                        this.hoveredIndex = null;
                                        chart.update('none'); // Immediate update for point size changes
                                        this.animateHoverOpacity(0, chart);

                                        // Explicitly hide tooltip element to stay in sync
                                        let tooltipEl = document.getElementById('chart-' + this.id + '-' + this.effectiveSectionId + '-tooltip');
                                        if (tooltipEl) {
                                            tooltipEl.style.opacity = 0;
                                            tooltipEl.style.pointerEvents = 'none';
                                        }
                                    }
                                    this.hideTimeout = null;
                                }, 200);
                            }
                        }
                    },
                    animation: {
                        duration: this.skipAnimationForNextRender ? 0 : 1000,
                    },
                    responsive: true,
                    maintainAspectRatio: false,

                    animations: {
                        x: {
                            duration: 1000,
                        }
                    },
                    layout: {
                        padding: {
                            right: 50,
                            left: 10,
                            bottom: 10
                        }
                    },
                    indexAxis: 'x',
                    scales: {
                        x: {
                            grid: {
                                drawBorder: false,
                                display : false,
                            },
                            border: {
                                display: false,
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: 'DM Sans',
                                },
                                maxRotation:0,
                                autoSkip:false,
                                callback: function(value, index, ticks, context) {
                                    // Wrap around long labels
                                    var label_substrings = '';
                                    if(this.getLabelForValue(value)){
                                        let label = this.getLabelForValue(value) + "";
                                        label_substrings = label.split(" ");
                                    }

                                    for(var i = 0; i <label_substrings.length-1; i++){
                                        if ((label_substrings[i].length + 1 + label_substrings[i+1].length) < 15){
                                            // if two substrings have total length < 15, concat them together
                                            // Note: length=15 is just a temporary value which makes the chart looks fine with dummy data
                                            label_substrings[i] = label_substrings[i]+' '+label_substrings[i+1]
                                            label_substrings[i+1] = ''
                                        }
                                    }
                                    return label_substrings;
                                }
                            }
                        },
                        y: {
                            display: true,
                            position: 'left',
                            beginAtZero: true,
                            // Ensure there's always space above the highest bar
                            max: this.getYAxisMax(this.max_bar_value),
                            border: {
                                display: false,
                                width: 2,
                                dash: [5,5],
                                dashOffset: 0,
                            },
                            grid: {
                                drawOnChartArea: true,
                                drawBorder: false,

                                color: 'rgba(206, 219, 215, 1)',
                                lineWidth: 1, // gridline thickness
                                borderDash: [2,2], // gridline in the chart area that is perpendicular to this axis

                                drawTicks: true,
                                tickColor: 'rgba(206, 219, 215, 1)',
                                tickLength: 20,
                                tickBorderDash: [2,2],
                                tickBorderDashOffset: 0,
                                z: -2,
                            },
                            //For the ticks on the axes, format the numbers to make the pretty
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: 'DM Sans',
                                },
                                // color: 'rgba(57, 74, 69, 1.0)',
                                offset: false,
                                autoSkip: true,
                                maxTicksLimit: 5,
                                callback: function(value, index, ticks, context) {
                                    if(self.format_type[0] == 'money') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.currency_abbr(value);
                                        }
                                        return self.currency_with_zero(value);
                                    }
                                    if(self.format_type[0] == 'number') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.num_abbr(value);
                                        }
                                        if(self.max_value >= 10) {
                                            return self.number_with_zero(value);
                                        }
                                        return self.number_with_decimal(value);
                                    }
                                    if(self.format_type[0] == 'percent')
                                        return self.percentage(value);
                                }
                            },
                        },
                        y1: {
                            display: true,
                            position: 'right',
                            beginAtZero: true,
                            border: {
                                display: false,
                                width: 2,
                                dash: [5,5],
                                dashOffset: 0,
                            },
                            grid: {
                                /// TODO: Hide bottom (first) y-axis gridline on additional y-axis if there is more than one y-axis. This will prevent both dashed lines from appearing on the bottom level.

                                drawOnChartArea: true,
                                drawBorder: false,

                                color: 'rgba(206, 219, 215, 1)',
                                lineWidth: 1, // gridline thickness
                                borderDash: [2,2], // gridline in the chart area that is perpendicular to this axis

                                drawTicks: true,
                                tickColor: 'rgba(206, 219, 215, 1)',
                                tickLength: 20,
                                tickBorderDash: [2,2],
                                tickBorderDashOffset: 0,
                                z: -2,
                            },
                            //For the ticks on the axes, format the numbers to make the pretty
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: 'DM Sans',
                                },
                                offset: false,
                                color: 'rgba(57, 74, 69, 1.0)',
                                autoSkip: true,
                                maxTicksLimit: 5,
                                callback: function(value, index, ticks, context) {
                                    if(self.format_type[1] == 'money') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.currency_abbr(value);
                                        }
                                        return self.currency_with_zero(value);
                                    }
                                    if(self.format_type[1] == 'number') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.num_abbr(value);
                                        }
                                        if(self.max_value >= 10) {
                                            return self.number_with_zero(value);
                                        }
                                        return self.number_with_decimal(value);
                                    }
                                    if(self.format_type[1] == 'percent')
                                        return self.percentage(value);
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            //Only show the legend if there is more than one media type
                            position: (data.datasets.length > 1) ? 'bottom' : 'none',
                            align: 'center',
                            fullSize: true,
                            onClick: (e, legendItem, legend) => {
                                const chart = legend.chart;
                                const datasetIndex = legendItem.datasetIndex;

                                chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
                                chart.update();
                                const self = this;
                                self.datasetVisibility = chart.data.datasets.map((_, i) => chart.isDatasetVisible(i));
                            },
                            onHover: function(e, chart) {
                                e.native.target.style.cursor = 'pointer';
                            },
                            onLeave: function(e, chart) {
                                e.native.target.style.cursor = 'default';
                            },
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'line',
                                pointStyleWidth: 20,
                                lineWidth: 2,
                                padding: 30,
                                generateLabels: (chart) => chart.data.datasets.map(this.getLegendLabelStylesByDatasetType),
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: 'DM Sans',
                                },
                            }
                        },
                        tooltip: {
                            enabled: false,
                            external: function(context) {
                                const chartCanvasId = context.chart?.canvas?.id;
                                if (!chartCanvasId || !chartCanvasId.startsWith('chart-')) return;

                                const chartIdFromCanvas = chartCanvasId.replace(/^chart-/, '');
                                const expectedTooltipId = self.getTooltipId();
                                if (chartIdFromCanvas !== String(self.id)) {
                                    let tooltipEl = document.getElementById(expectedTooltipId);
                                    if (tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                    }
                                    return;
                                }

                                let tooltipEl = self.getOrCreateTooltipElement();
                                if (!tooltipEl) return;

                                const tooltipIdPrefix = 'chart-' + chartIdFromCanvas + '-';
                                if (!tooltipEl.id.startsWith(tooltipIdPrefix) || !tooltipEl.id.endsWith('-tooltip')) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }
                                if (tooltipEl.id !== expectedTooltipId) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }

                                const tooltipModel = context.tooltip;

                                if (!tooltipModel || tooltipModel.opacity === 0) {
                                    if (!self.isTooltipHovered && self.hoveredIndex === null && tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                        self.hideAllChartTooltips();
                                    }
                                    return;
                                }

                                const currentDataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0
                                    ? tooltipModel.dataPoints[0].dataIndex
                                    : null;

                                if (currentDataIndex === null || currentDataIndex !== self.hoveredIndex) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    self.hideAllChartTooltips();
                                    return;
                                }

                                self.hideOtherTooltips(tooltipEl);

                                // Apply base styles using mixin method
                                self.applyTooltipBaseStyles(tooltipEl);

                                // Set Text
                                if (tooltipModel.body) {
                                    const titleLines = tooltipModel.title || [];
                                    const bodyLines = tooltipModel.body.map(b => b.lines);

                                    let innerHtml = '<thead>';
                                    innerHtml += '<tr><th style="font-weight: 500; font-size: 18px; padding-bottom: 4px;">' + titleLines[0] + '</th></tr>';
                                    innerHtml += '</thead><tbody>';

                                    let tooltipBody = '';
                                    // format_type for Combo chart: [bar_format, line_format]
                                    // tooltipModel.dataPoints is in order of datasets
                                    
                                    bodyLines.forEach((body, i) => {
                                        if (!body || !body[0]) return;

                                        const body_label_temp = body[0].split(':');
                                        const body_label = body_label_temp.slice(0, body_label_temp.length - 1).join(':').trim();
                                        const value = tooltipModel.dataPoints[i]?.raw;

                                        if (value === undefined) return;

                                        const colors = tooltipModel.labelColors[i];
                                        let textColor = colors.backgroundColor;
                                        
                                        // If filter is applied and hovering over a different category, use gray for labels
                                        if (self.if_chart_filter_applied && self.chart_filter_label.length > 0) {
                                            const filteredCategory = self.chart_filter_label[0];
                                            const hoveredCategory = titleLines[0];
                                            if (filteredCategory !== hoveredCategory) {
                                                // Use gray color instead of metric color
                                                textColor = 'rgb(156, 163, 175)'; // Gray color
                                            }
                                        }
                                        
                                        // Bar is index 0, lines are index > 0
                                        const formatType = tooltipModel.dataPoints[i].datasetIndex === 0 ? self.format_type[0] : self.format_type[1];

                                        let formattedValue = '';
                                        if (formatType === 'money') {
                                            formattedValue = self.currency_with_zero(value);
                                        } else if (formatType === 'number' && self.max_value >= 10) {
                                            formattedValue = self.number_with_zero(value);
                                        } else if (formatType === 'number' && self.max_value < 10) {
                                            formattedValue = self.number_with_decimal(value);
                                        } else if (formatType === 'percent') {
                                            formattedValue = self.percentage(value);
                                        }

                                        tooltipBody += '<tr><td class="d-flex align-items-center" style="padding-top: 1px; padding-bottom: 1px;"><span style="color: ' + textColor + '; font-weight: 400;">' + body_label + '</span>: <span style="color: #111921; font-weight: 400; margin-left: 4px;">' + formattedValue + '</span></td></tr>';
                                    });

                                    innerHtml += tooltipBody;

                                    if (self.if_on_report_page && !self.if_benchmark_report) {
                                        const isFilterApplied = self.if_chart_filter_applied && 
                                                             self.chart_filter_label?.[0] === titleLines[0] &&
                                                             (self.chart_filter_label.length === 1);

                                        innerHtml += '<div class="d-flex justify-content-end my-2">' +
                                            '<button class="btn btn-sm btn-primary d-flex align-items-center px-4" style="font-weight: 500;">' +
                                            '<img class="icon me-1" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">' + (isFilterApplied ? 'Remove as Filter' : 'Filter') +
                                            '</button>' +
                                            '</div>';

                                        innerHtml += '</tbody>';

                                        const tableRoot = tooltipEl.querySelector('table');
                                        if (tableRoot) {
                                            tableRoot.innerHTML = innerHtml;

                                            // Main filter button
                                            const btn = tooltipEl.querySelector('.btn-primary');
                                            if (btn) {
                                                const isFilterOnOtherChart = self.applied_chart_filter?.if_chart_filter_applied && 
                                                                             !self.if_chart_filter_applied;
                                                const isFilterOnThisChart = self.if_chart_filter_applied;
                                                let isDifferentSelection = false;
                                                
                                                if (isFilterOnThisChart) {
                                                    const hoveredBar = titleLines[0];
                                                    const filteredBar = self.chart_filter_label?.[0];
                                                    if (hoveredBar !== filteredBar) {
                                                        isDifferentSelection = true;
                                                    }
                                                }
                                                
                                                btn.disabled = isFilterOnOtherChart || (isFilterOnThisChart && isDifferentSelection);

                                                if (self.if_chart_filter_applied) {
                                                    btn.addEventListener('click', () => {
                                                        self.if_chart_filter_applied = false;
                                                        self.chart_filter_label = [];
                                                        self.$emit('exploreChart', {
                                                            chart_selected: null,
                                                            chart_filters: []
                                                        });
                                                    });
                                                } else {
                                                    btn.addEventListener('click', (event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        
                                                        self.if_chart_filter_applied = true;
                                                        self.chart_filter_label = [];
                                                        const filter_array = [];
                                                        
                                                        self.chart_selected?.group_by?.forEach((dimension, index) => {
                                                            if (index === 0) {
                                                                const value_temp = titleLines[0];
                                                                if (value_temp) {
                                                                    self.chart_filter_label.push(value_temp);
                                                                    filter_array.push({
                                                                        name: dimension.text,
                                                                        column: dimension.value,
                                                                        value: value_temp
                                                                    });
                                                                }
                                                            }
                                                        });

                                                        if (filter_array.length > 0) {
                                                            self.$emit('exploreChart', {
                                                                chart_selected: self.chart_selected,
                                                                chart_filters: filter_array
                                                            });
                                                        }
                                                    });
                                                }
                                            }
                                        }
                                    } else {
                                        innerHtml += '</tbody>';
                                        const tableRoot = tooltipEl.querySelector('table');
                                        if (tableRoot) {
                                            tableRoot.innerHTML = innerHtml;
                                        }
                                    }
                                }
                                
                                // Position tooltip using mixin method
                                self.positionTooltipOnSide(tooltipEl, context, currentDataIndex);
                            }
                        },
                        datalabels: {
                            anchor: function(context) {
                                if (context.datasetIndex !== 0) return 'center';
                                const value = context.dataset.data[context.dataIndex];
                                if (value == null) return 'center';
                                return value >= 0 ? 'end' : 'start';
                            },
                            align: function(context) {
                                if (context.datasetIndex !== 0) {
                                    const index = context.datasetIndex;
                                    // For line datasets, alternate positions to avoid collision
                                    const positions = [-60, 120, 60, 240];
                                    return positions[index % 4];
                                }
                                const value = context.dataset.data[context.dataIndex];
                                if (value == null) return 'center';
                                return value >= 0 ? 'end' : 'start';
                            },
                            offset: function(context) {
                                return context.datasetIndex === 0 ? -2 : 4;
                            },
                            color: function(context) {
                                const baseColor = '#5f687c';
                                
                                // If labels are always shown (toggle ON), return full opacity
                                if (self.if_show_label) {
                                    const dataset = context.dataset || {};
                                    
                                    // Check if this point should be highlighted due to filter
                                    if (self.if_chart_filter_applied && self.chart_filter_label.length > 0) {
                                        const chart_filter_index = self.chart_selected.labels.findIndex(label => label == self.chart_filter_label[0]);
                                        
                                        // If this is the filtered point, use metric color
                                        if (context.dataIndex === chart_filter_index) {
                                            // For bar dataset (index 0), use gray color
                                            if (context.datasetIndex === 0) {
                                                return self.getColorByLetter('gray', 'A');
                                            }
                                            // For line datasets, use the line color
                                            return self.getColorByLetter(self.color[1], 'A');
                                        } else {
                                            // For non-filtered points, use gray
                                            return 'rgb(156, 163, 175)';
                                        }
                                    }
                                    
                                    // Default color logic when no filter is applied
                                    // For bar dataset (index 0), use gray color
                                    if (context.datasetIndex === 0) {
                                        return self.getColorByLetter('gray', 'A');
                                    }
                                    // For line datasets, use the line color
                                    return self.getColorByLetter(self.color[1], 'A');
                                }
                                
                                // For hover labels when toggle is OFF, apply animated opacity
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return `rgba(17, 25, 33, ${self.hoverOpacity})`;
                                }
                                
                                // For large/x-large charts when toggle is OFF, use metric color for line datasets
                                const chartSize = self.size || self.chart_selected?.size || 'small';
                                if ((chartSize === 'large' || chartSize === 'x-large') && context.datasetIndex > 0) {
                                    // Line datasets (index > 0) should use metric color
                                    return self.getColorByLetter(self.color[1], 'A');
                                }
                                
                                return baseColor;
                            },
                            //backgroundColor: 'rgba(255, 255, 255, 0.4)', // data label background contrast
                            font: {
                                size: this.font_size,
                            },
                            display: function(context) {
                                // Always show labels if toggle is ON
                                if (self.if_show_label) {
                                    // When toggle is ON, use original logic for which labels to show
                                    // Original logic for non-hover state
                                    if (self.chart_selected.show_all_data_labels && context.datasetIndex == 0){
                                        // for bar line combo chart, if show all data labels, only show the labels for lines
                                        return false;
                                    }
                                    if (!self.chart_selected.show_all_data_labels && context.datasetIndex < context.chart.data.datasets.length-1)
                                        return false;

                                    if (self.if_chart_filter_applied){
                                        var chart_filter_index = self.chart_selected.labels.findIndex((label) => {
                                            if (Array.isArray(self.chart_filter_label)) {
                                                return self.chart_filter_label.includes(label);
                                            }
                                            return label == self.chart_filter_label;
                                        });
                                        if(context.dataIndex != chart_filter_index)
                                            return false;
                                    }

                                    // Respect legend-hidden state
                                    const meta = context.chart.getDatasetMeta(context.datasetIndex);
                                    return !meta || !meta.hidden;
                                }
                                
                                // Show labels for hovered group when toggle is OFF
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return true;
                                }
                                
                                // Use shouldShowDataLabels() to determine base visibility
                                if (!self.shouldShowDataLabels()) {
                                    return false;
                                }
                                
                                // Additional logic for time-based charts with > 15 data points
                                // When shouldShowDataLabels() returns true but we have > 15 points,
                                // show labels only for the most recent year
                                const chartSize = self.size || self.chart_selected?.size || 'small';
                                const totalLabels = self.chart_selected.labels?.length || 0;
                                
                                if ((chartSize === 'large' || chartSize === 'x-large') && self.isTimeBased() && totalLabels > 15) {
                                    const mostRecentIndices = self.getMostRecentPeriodIndices();
                                    // Adjust indices based on currentPageStart for truncated data (though large/x-large shouldn't truncate)
                                    const adjustedIndices = mostRecentIndices.map(idx => idx - self.currentPageStart);
                                    return adjustedIndices.includes(context.dataIndex);
                                }
                                
                                // For all other cases where shouldShowDataLabels() returns true, show the label
                                return true;
                            },
                            // clamp: true,
                            formatter: function(value, context) {
                                // When toggle is ON, always format and return the value
                                if (self.if_show_label) {
                                    return self.formatDataLabelValue(
                                        value, 
                                        self.format_type, 
                                        context.datasetIndex,
                                        false
                                    );
                                }
                                
                                // When hovering, show labels for all datasets at the hovered index
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return self.formatDataLabelValue(
                                        value, 
                                        self.format_type, 
                                        context.datasetIndex,
                                        false
                                    );
                                }

                                // Original logic for non-hover state when toggle is OFF
                                if (self.chart_selected.show_all_data_labels && context.datasetIndex == 0){
                                    // for bar line combo chart, if show all data labels, only show the labels for lines
                                    return null;
                                }
                                if (!self.chart_selected.show_all_data_labels && context.datasetIndex < context.chart.data.datasets.length-1)
                                    return null;

                                if (self.if_chart_filter_applied){
                                    var chart_filter_index = self.chart_selected.labels.findIndex((label) => {
                                        if (Array.isArray(self.chart_filter_label)) {
                                            return self.chart_filter_label.includes(label);
                                        }
                                        return label == self.chart_filter_label;
                                    });
                                    if(context.dataIndex != chart_filter_index)
                                        return null;
                                }

                                // Format the value
                                return self.formatDataLabelValue(
                                    value, 
                                    self.format_type, 
                                    context.datasetIndex,
                                    false
                                );
                            },
                        }
                    },
                    title: {
                        display: false,
                    },
                }
            };
            return config;
        }
    }
});
</script>